import ModalLayout from "@/components/layout/ModalLayout";
import { Button } from "@/components/ui/Button";
import { InputBox } from "@/components/ui/InputBox";
import ModalHeader from "@/components/ui/ModalHeader";
import SelectBox from "@/components/ui/SelectBox";
import { useCodeList, useCodeUpdate } from "@/hooks";
import { useState, useEffect } from "react";

interface UpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedData?: any; // 선택된 행 데이터
  onAfterUpdate?: (updated: any) => void;
}

export default function UpdateModal({ isOpen, onClose, selectedData, onAfterUpdate }: UpdateModalProps) {
  // form states
  const [category, setCategory] = useState(""); // "1" 대분류 / "2" 소분류 (읽기전용)
  const [bigCategory, setBigCategory] = useState(""); // 상위 분류 id (읽기전용)
  const [code, setCode] = useState("");
  const [codeName, setCodeName] = useState("");
  const [sortOrder, setSortOrder] = useState(""); // 표시용 문자열
  const [useYn, setUseYn] = useState<"Y" | "N">("Y");
  const [remark, setRemark] = useState("");
  const [bigCategoryOptions, setBigCategoryOptions] = useState<Array<{ label: string; value: string }>>([]);
  const [errors, setErrors] = useState({
    code: false,
    codeName: false,
    sortOrder: false,
  });
  const [touched, setTouched] = useState({
    code: false,
    codeName: false,
    sortOrder: false,
  });

  const { mutate: updateCode } = useCodeUpdate();
  const { data: bigCategoryData } = useCodeList({ parentId: undefined });

  const useYnOptions = [
    { label: "사용", value: "Y" },
    { label: "미사용", value: "N" },
  ];
  const categoryOptions = [
    { label: "대분류", value: "1" },
    { label: "소분류", value: "2" },
  ];

  // 모달 오픈 시: 대분류 옵션(읽기전용 표시용)
  useEffect(() => {
    if (!isOpen) return;
    if (Array.isArray(bigCategoryData)) {
      setBigCategoryOptions(
        bigCategoryData.map((item: any) => ({
          label: item.name,
          value: String(item.codeId),
        }))
      );
    } else {
      setBigCategoryOptions([]);
    }
  }, [isOpen, bigCategoryData]);

  // 모달 오픈 시: selectedData로 폼 초기화
  useEffect(() => {
    if (!isOpen || !selectedData) return;

    setCategory(selectedData.parentId ? "2" : "1");
    setBigCategory(selectedData.parentId?.toString() || "");
    setCode(selectedData.code || "");
    setCodeName(selectedData.name || selectedData.codeName || "");
    setSortOrder(selectedData.sortOrder?.toString() || "");
    setUseYn(selectedData.useYn || "Y");
    setRemark(selectedData.remark || "");

    setErrors({ code: false, codeName: false, sortOrder: false });
    setTouched({ code: false, codeName: false, sortOrder: false });
  }, [isOpen, selectedData]);

  // 저장
  const handleSave = () => {
    setTouched({ code: true, codeName: true, sortOrder: true });

    const so = Number(sortOrder);
    const nextErrors = {
      code: !code.trim(),
      codeName: !codeName.trim(),
      sortOrder: !sortOrder.trim() || !Number.isInteger(so) || so <= 0,
    };
    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) return;

    const updateData = {
      codeId: selectedData.codeId,
      parentId: category === "1" ? undefined : bigCategory ? parseInt(bigCategory, 10) : undefined, // 읽기전용이지만 서버 업데이트엔 포함
      code: code.trim(),
      name: codeName.trim(),
      sortOrder: so,
      useYn,
      remark: remark.trim(),
    };

    updateCode(updateData, {
      onSuccess: (res) => {
        const updated = res?.data ?? updateData;
        onAfterUpdate?.(updated);
        onClose();
      },
    });
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <ModalLayout isOpen={isOpen} onClose={onClose} title="공통코드 수정">
      <ModalHeader title="기본 정보" />
      {/* 분류/상위분류*/}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-3 mb-4">
        <div className="space-y-4">
          <SelectBox
            label="분류 구분"
            value={category}
            onChange={() => {}}
            options={categoryOptions}
            disabled
          />
        </div>
        <div className="space-y-4">
          <SelectBox
            label="상위 분류"
            value={bigCategory}
            onChange={() => {}}
            options={bigCategoryOptions}
            disabled
          />
        </div>
      </div>

      {/* 코드/명칭 */}
      <div className="flex gap-3 mb-4">
        <div className="w-1/3">
          <InputBox
            required
            label="분류 코드"
            value={code}
            onChange={e => {
              const v = e.target.value;
              setCode(v);
              setTouched(prev => ({ ...prev, code: true }));
              setErrors(prev => ({ ...prev, code: !v.trim() }));
            }}
            className={touched.code && errors.code ? "border-[#FF3232]" : ""}
          />
        </div>
        <div className="w-2/3">
          <InputBox
            required
            label="분류 명칭"
            value={codeName}
            onChange={e => {
              const v = e.target.value;
              setCodeName(v);
              setTouched(prev => ({ ...prev, codeName: true }));
              setErrors(prev => ({ ...prev, codeName: !v.trim() }));
            }}
            className={touched.codeName && errors.codeName ? "border-[#FF3232]" : ""}
          />
        </div>
      </div>

      {/* 비고 */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-3 mb-4">
        <div className="space-y-4">
          <InputBox label="비고" value={remark} onChange={e => setRemark(e.target.value)} />
        </div>
      </div>

      <ModalHeader title="추가 정보" />
      {/* 정렬순서 / 사용여부 */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-3">
        <div className="space-y-4">
          <InputBox
            required
            label="정렬순서 (1부터)"
            value={sortOrder}
            inputMode="numeric"
            onChange={e => {
              const v = e.target.value;
              setSortOrder(v);
              setTouched(prev => ({ ...prev, sortOrder: true }));
              const n = Number(v);
              const invalid = !v.trim() || !Number.isInteger(n) || n <= 0;
              setErrors(prev => ({ ...prev, sortOrder: invalid }));
            }}
            className={touched.sortOrder && errors.sortOrder ? "border-[#FF3232]" : ""}
          />
        </div>
        <div className="space-y-4">
          <SelectBox
            label="사용 여부"
            value={useYn}
            onChange={v => setUseYn(v as "Y" | "N")}
            options={useYnOptions}
          />
        </div>
      </div>

      {/* 푸터 */}
      <div className="flex items-center gap-3 mt-6">
        <div className="flex justify-start w-full">
          <Button variant="white" onClick={handleCancel} className="py-[10px]">
            취소
          </Button>
        </div>
        <div className="flex justify-end w-full">
          <Button variant="primary" onClick={handleSave} className="py-[10px]">
            저장
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
